import { useJSONModal } from 'modules/Predictions/components/PredictionSidebar/useUploadModal';
import { Button } from 'components/ui_library/Button';
import { Alert } from 'components/Alert';
import { ImageColorRecognizerView } from 'components/ModelOutputRenderer/components/ModelOutputTypeViews/ImageColorRecognizerView';
import { AudioPlayer } from 'components/Common/AudioPlayer/AudioPlayer'; // Import the new component
import { CollapsibleBar, CollapsibleBarProps } from './components/CollapsibleBar/CollapsibleBar';
import {
  ConceptWithoutRegionView,
  ConceptWithRegionView,
  ImageToTextView,
  TextTranslationView,
  OcrTextTranslationView,
  OcrOutput,
  ImageOutputView,
} from './components/ModelOutputTypeViews';
import {
  hasRegionsWithConcepts,
  hasRegions,
  hasConcepts,
  hasText,
  hasFrames,
  hasFramesWithRegions,
  hasColors,
  hasImages,
  hasAudio,
} from './components/utils';
import { modelOutputsStyles } from './ModelOutputRenderer.styles';

interface ModelOutputRendererProps {
  model: CF.API.Models.Model;
  modelOutput: CF.API.ModelOutputs.ModelOutput;
  ocrOutput?: OcrOutput;
  regionColorMap: Record<string, string>;
  isOutputSuppressed: boolean;
  collapsibleBarProps: Omit<CollapsibleBarProps, 'children' | 'isThresholdSliderVisible'>;
}

export const ModelOutputRenderer = (props: ModelOutputRendererProps): JSX.Element | null => {
  const { model, modelOutput, ocrOutput, isOutputSuppressed, collapsibleBarProps } = props;
  const { data } = modelOutput;
  const { id, user_id, app_id } = model;

  const openJsonModal = useJSONModal(
    {
      json: modelOutput,
    },
    'model_page',
  );

  const modelUrl = `https://clarifai.com/${user_id}/${app_id}/models/${id}`;
  const OutputSuppressedMessage = (
    <p className="output-suppressed">
      Model output is suppressed by the workflow owner. Try {'\u00A0'}
      <a href={modelUrl} target="_blank" rel="noreferrer">
        {modelUrl}
      </a>
      {'\u00A0'} instead to view output.
    </p>
  );
  const UnsupportedOutputMessage = <p>Model output is not supported in web viewer. Please click &ldquo;View Json&rdquo; to view raw output.</p>;
  const DisplayMessage = isOutputSuppressed ? OutputSuppressedMessage : UnsupportedOutputMessage;
  const InvalidOutputView = (
    <CollapsibleBar modelId={id} isThresholdSliderVisible={false} {...collapsibleBarProps} isBarCollapsed>
      <Alert type="info">{DisplayMessage}</Alert>
      <Button variant="secondary" size="sm" onClick={openJsonModal}>
        View JSON
      </Button>
    </CollapsibleBar>
  );

  const renderOutputView = (OutputView: JSX.Element): JSX.Element => <div className={modelOutputsStyles}>{OutputView}</div>;

  if (hasColors(data)) {
    const OutputView = <ImageColorRecognizerView {...props} />;
    return renderOutputView(OutputView);
  }

  // Model output contains concepts with image regions
  if (hasRegionsWithConcepts(data) || hasFramesWithRegions(data)) {
    const OutputView = <ConceptWithRegionView {...props} />;
    return renderOutputView(OutputView);
  }

  // Model output contains concepts without image regions
  if (hasConcepts(data) || hasFrames(data)) {
    const OutputView = <ConceptWithoutRegionView {...props} />;
    return renderOutputView(OutputView);
  }

  // OCR models (image to text)
  if (hasRegions(data)) {
    const OutputView = <ImageToTextView {...props} />;
    return renderOutputView(OutputView);
  }

  // Translation models (text to text)
  if (hasRegions(data) && ocrOutput) {
    const OutputView = <OcrTextTranslationView ocrOutput={ocrOutput} {...props} />;
    return renderOutputView(OutputView);
  }

  // Translation models (text to text)
  if (hasText(data)) {
    const OutputView = <TextTranslationView {...props} />;
    return renderOutputView(OutputView);
  }

  if (hasImages(data)) {
    const OutputView = <ImageOutputView data={data} />;
    return renderOutputView(OutputView);
  }

  if (hasAudio(data)) {
    // Use unknown and type assertion for safer access after the check
    const audioData = data as unknown as { audio: { base64: string } };
    const OutputView = <AudioPlayer base64Data={audioData.audio.base64} />;
    return renderOutputView(OutputView);
  }

  return renderOutputView(InvalidOutputView);
};
